/**
 * The header's connect control.
 *
 * RainbowKit's button, wearing the portal's own clothes: `ConnectButton.Custom`
 * hands over the state and the modal openers but renders nothing itself, so the
 * wallet list, QR code and deep links are all RainbowKit's while the header
 * keeps its markup, its classes and its Indonesian copy.
 */
import { ConnectButton as RainbowConnectButton } from "@rainbow-me/rainbowkit";

import { BSC_TESTNET_CHAIN_ID, CHAIN_ID, NETWORK_NAME } from "@/lib/config";
import { shortenAddress } from "@/lib/format";

import { useWallet } from "./WalletProvider";

export function ConnectButton() {
  const {
    connecting,
    disconnect,
    networkSwitchUrl,
    switchNetwork,
    switchingNetwork,
  } = useWallet();

  return (
    <RainbowConnectButton.Custom>
      {({ account, chain, openAccountModal, openConnectModal, mounted }) => {
        // RainbowKit can mount one render before wagmi has restored a persisted
        // session. Keep a stable skeleton in that gap instead of flashing a
        // connect action that is not actually the user's state.
        const ready = mounted && !connecting;
        const connected = ready && Boolean(account) && Boolean(chain);

        if (!ready) {
          return (
            <div
              className="wallet-actions wallet-actions--loading"
              role="status"
              aria-label="Memulihkan koneksi wallet"
              aria-live="polite"
            >
              <span className="wallet-button wallet-button--skeleton" aria-hidden />
            </div>
          );
        }

        return (
          <div
            className={`wallet-actions ${
              connected && chain!.unsupported ? "wallet-actions--network-mismatch" : ""
            }`}
          >
            {!connected ? (
              <button
                type="button"
                onClick={openConnectModal}
                aria-haspopup="dialog"
                aria-label="Hubungkan wallet"
                className="wallet-button wallet-button--primary"
              >
                <span className="wallet-button__label--wide">Hubungkan Wallet</span>
                <span className="wallet-button__label--compact">Hubungkan</span>
              </button>
            ) : chain!.unsupported ? (
              // RainbowKit's own chain modal swallows switchChain failures
              // silently and auto-closes on any outcome — on mobile wallets
              // that don't support wallet_switchEthereumChain, that reads as
              // the button doing nothing. `switchNetwork` goes through the
              // portal's own handling instead, which surfaces a reason via
              // `walletError` (see ClaimCard) when the wallet can't comply.
              <>
                {networkSwitchUrl ? (
                  <a
                    href={networkSwitchUrl}
                    aria-label={`Buka portal di Trust Wallet untuk menggunakan ${NETWORK_NAME}`}
                    className="wallet-button wallet-button--danger"
                  >
                    <span className="wallet-button__label--wide">Buka di Trust Wallet</span>
                    <span className="wallet-button__label--compact">Buka Trust</span>
                  </a>
                ) : (
                  <button
                    type="button"
                    onClick={switchNetwork}
                    disabled={switchingNetwork}
                    aria-busy={switchingNetwork}
                    aria-label={
                      switchingNetwork
                        ? "Menunggu konfirmasi perpindahan jaringan di wallet"
                        : `Pindah ke ${NETWORK_NAME}`
                    }
                    className="wallet-button wallet-button--danger"
                  >
                    {switchingNetwork ? (
                      <span className="wallet-button__spinner" aria-hidden />
                    ) : null}
                    <span className="wallet-button__label--wide">
                      {switchingNetwork ? "Menunggu wallet..." : `Pindah ke ${NETWORK_NAME}`}
                    </span>
                    <span className="wallet-button__label--compact">
                      {switchingNetwork
                        ? "Menunggu..."
                        : CHAIN_ID === BSC_TESTNET_CHAIN_ID
                          ? "Pindah Testnet"
                          : "Pindah BSC"}
                    </span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={disconnect}
                  aria-label="Putuskan wallet"
                  title="Putuskan wallet"
                  className="wallet-button wallet-button--quiet wallet-button--disconnect"
                >
                  <span className="wallet-button__label--wide">Putuskan</span>
                  <span className="wallet-button__label--compact">Keluar</span>
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={openAccountModal}
                aria-haspopup="dialog"
                title="Kelola koneksi wallet"
                className="wallet-button wallet-button--connected font-mono"
              >
                <span className="wallet-button__status" aria-hidden />
                {shortenAddress(account!.address)}
              </button>
            )}
          </div>
        );
      }}
    </RainbowConnectButton.Custom>
  );
}

/** Persistent feedback for a network request that may move the user to another app. */
export function WalletNetworkNotice() {
  const { error, networkSwitchUrl, switchingNetwork, clearError } = useWallet();

  if (!switchingNetwork && !error) return null;

  return (
    <div
      className={`wallet-network-notice ${error ? "wallet-network-notice--error" : ""}`}
      role={error ? "alert" : "status"}
      aria-live="polite"
    >
      <span
        className={`wallet-network-notice__icon ${
          switchingNetwork ? "wallet-network-notice__icon--loading" : ""
        }`}
        aria-hidden
      >
        {switchingNetwork ? null : "!"}
      </span>
      <div>
        <strong>
          {error ? "Jaringan belum berubah" : "Konfirmasi perpindahan di wallet"}
        </strong>
        <p>
          {error ??
            `Buka aplikasi wallet dan setujui perpindahan ke ${NETWORK_NAME}. Setelah itu kembali ke portal.`}
        </p>
        {error && networkSwitchUrl ? (
          <a href={networkSwitchUrl} className="wallet-network-notice__action">
            Buka di Trust Wallet
          </a>
        ) : null}
      </div>
      {error ? (
        <button type="button" onClick={clearError} aria-label="Tutup pesan jaringan">
          Tutup
        </button>
      ) : null}
    </div>
  );
}
